// client/src/components/ModulesTab.jsx
// "מודולים" tab — opt-in add-ons a manager can enable + configure per camp.
// First (and currently only) module: attendance. Each module is a self-
// contained card; new modules slot in as more cards.

import { useState, useEffect } from 'react';
import { useModules, useUpdateModule } from '../hooks/useModules.js';
import { useDevices } from '../hooks/useDevices.js';
import Spinner from './Spinner.jsx';
import TimeSelect from './TimeSelect.jsx';
import { CalendarDaysIcon, DevicePhoneMobileIcon } from './Icons.jsx';

export default function ModulesTab({ campId }) {
  const modulesQ = useModules(campId);

  if (modulesQ.isLoading) return <Spinner label="טוען מודולים..." />;
  if (modulesQ.isError) {
    return <div className="card-danger p-4 text-sm text-joy-900">שגיאה בטעינת המודולים.</div>;
  }

  const attendance = (modulesQ.data || []).find((m) => m.key === 'attendance');

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm text-ink-600">
        <span className="w-1.5 h-1.5 rounded-full bg-brand-500" />
        מודולים הם תוספות שאפשר להפעיל ולהגדיר לכל תכנית בנפרד.
      </div>
      {attendance && <AttendanceModule campId={campId} module={attendance} />}
    </div>
  );
}

/* ───── Attendance module ───────────────────────────────────── */

function AttendanceModule({ campId, module }) {
  const devicesQ = useDevices(campId);
  const update = useUpdateModule(campId);
  const kiosks = (devicesQ.data || []).filter((d) => d.kind === 'KIOSK');

  const cfg = module.config || {};
  const [enabled, setEnabled] = useState(module.enabled);
  const [startTime, setStartTime] = useState(cfg.startTime || '07:30');
  const [endTime, setEndTime] = useState(cfg.endTime || '08:30');
  const [points, setPoints] = useState(cfg.points ?? 10);
  const [label, setLabel] = useState(cfg.label || 'נוכחות');
  const [deviceIds, setDeviceIds] = useState(Array.isArray(cfg.deviceIds) ? cfg.deviceIds : []);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState(null);

  // Clear the "saved" confirmation whenever the form changes again.
  useEffect(() => { setSaved(false); }, [enabled, startTime, endTime, points, label, deviceIds]);

  function toggleDevice(id) {
    setDeviceIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  async function save() {
    setErr(null);
    if (enabled) {
      const n = Number(points);
      if (!Number.isInteger(n) || n < 1) { setErr('נקודות חייב להיות מספר שלם חיובי.'); return; }
      if (startTime >= endTime) { setErr('שעת ההתחלה חייבת להיות לפני שעת הסיום.'); return; }
    }
    try {
      await update.mutateAsync({
        key: 'attendance',
        enabled,
        config: {
          startTime,
          endTime,
          points: Number(points) || 1,
          label: label.trim() || 'נוכחות',
          deviceIds: deviceIds.filter((id) => kiosks.some((k) => k.id === id)),
        },
      });
      setSaved(true);
    } catch (e) {
      setErr(e?.code === 'validation_failed' ? 'בדקו את הערכים שהוזנו.' : (e?.message || 'שגיאה בשמירה.'));
    }
  }

  return (
    <div className="card overflow-hidden">
      <div className="h-1.5 bg-brand-gradient" />

      <div className="p-5">
        {/* Header */}
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-brand-gradient text-white grid place-items-center shrink-0 shadow-card">
            <CalendarDaysIcon className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-bold text-ink-900 text-lg">נוכחות</h3>
              <span className={enabled ? 'badge-mint' : 'badge-slate'}>{enabled ? 'פעיל' : 'כבוי'}</span>
            </div>
            <p className="text-sm text-ink-600 mt-1 max-w-xl leading-relaxed">
              תלמיד שמעביר כרטיס בעמדת שירות עצמי בטווח השעות שתגדירו — מזוכה אוטומטית בנקודות,
              פעם אחת בכל יום.
            </p>
          </div>
          <Toggle checked={enabled} onChange={setEnabled} label="הפעלת מודול נוכחות" />
        </div>

        {/* Config — shown once enabled */}
        {enabled && (
          <div className="mt-5 pt-5 border-t border-ink-100 space-y-5">
            <Field label="טווח שעות פעילות" hint="לפי שעון ישראל">
              <div className="flex items-center gap-2 flex-wrap">
                <TimeSelect value={startTime} onChange={setStartTime} />
                <span className="text-ink-400 text-sm font-bold">עד</span>
                <TimeSelect value={endTime} onChange={setEndTime} />
              </div>
            </Field>

            <Field label="נקודות לזיכוי">
              <input
                type="number" min="1" step="1" inputMode="numeric"
                className="input num w-32"
                value={points}
                onChange={(e) => setPoints(e.target.value)}
              />
            </Field>

            <Field label="תיאור בהיסטוריה" hint="כך תיראה הפעולה בכרטיס התלמיד">
              <input
                type="text" maxLength={40}
                className="input"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            </Field>

            {/* Stations */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <DevicePhoneMobileIcon className="w-4 h-4 text-ink-500" />
                <span className="text-sm font-bold text-ink-800">עמדות שירות עצמי</span>
              </div>
              {kiosks.length === 0 ? (
                <div className="rounded-2xl bg-gold-50 border border-gold-200 text-gold-900 px-4 py-3 text-sm">
                  אין עדיין עמדת שירות עצמי (קיוסק) בתכנית. צרו עמדת קיוסק במסך "מכשירים" כדי
                  שהמודול יפעל.
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2">
                    {kiosks.map((k) => (
                      <button
                        key={k.id}
                        type="button"
                        onClick={() => toggleDevice(k.id)}
                        className={deviceIds.includes(k.id) ? 'chip-active' : 'chip'}
                      >
                        {k.name}
                      </button>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-ink-500">
                    {deviceIds.length === 0
                      ? 'חל על כל עמדות השירות העצמי בתכנית. בחרו עמדות כדי להגביל.'
                      : `חל על ${deviceIds.length} עמדות נבחרות.`}
                  </p>
                </>
              )}
            </div>

            {err && (
              <div className="rounded-2xl bg-joy-50 border border-joy-200 text-joy-900 px-4 py-3 text-sm font-medium">
                {err}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer — save is always available, so toggling the module off persists too */}
      <div className="px-5 py-3 bg-ink-50/60 border-t border-ink-100 flex items-center justify-end gap-3">
        {saved && <span className="text-sm font-semibold text-mint-700">נשמר ✓</span>}
        {err && !enabled && <span className="text-sm text-joy-700">{err}</span>}
        <button type="button" className="btn-primary" onClick={save} disabled={update.isPending}>
          {update.isPending ? 'שומר...' : 'שמירת הגדרות'}
        </button>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <div className="flex items-baseline gap-2 mb-1.5">
        <span className="text-sm font-bold text-ink-800">{label}</span>
        {hint && <span className="text-xs text-ink-400">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

// Accessible 24px on/off switch. RTL-natural: off → inline-start, on → inline-end.
function Toggle({ checked, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`relative shrink-0 w-[52px] h-7 rounded-full transition-colors duration-200 ${
        checked ? 'bg-mint-500' : 'bg-ink-300'
      }`}
    >
      <span
        className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-all duration-200 ${
          checked ? 'end-1' : 'start-1'
        }`}
      />
    </button>
  );
}
